import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useCallback, useRef, forwardRef, useImperativeHandle, useEffect } from 'react'

export interface RecaptchaVerifierProps {
  onVerify: (token: string) => void
  onError?: (error: Error) => void
  action?: string
}

export interface RecaptchaVerifierRef {
  executeRecaptcha: () => Promise<string | null>
}

const RecaptchaVerifier = forwardRef<RecaptchaVerifierRef, RecaptchaVerifierProps>(
  ({ onVerify, onError, action = 'submit' }, ref) => {
    const { executeRecaptcha } = useGoogleReCaptcha()
    const isMountedRef = useRef(true)

    const handleReCaptchaVerify = useCallback(async (): Promise<string | null> => {
      try {
        if (!isMountedRef.current) return null

        if (!executeRecaptcha) {
          throw new Error('reCAPTCHA가 아직 로드되지 않았습니다.')
        }

        const token = await executeRecaptcha(action)

        if (!isMountedRef.current) return null

        if (!token || typeof token !== 'string') {
          throw new Error('reCAPTCHA 토큰 생성 실패: 유효하지 않은 응답')
        }

        onVerify(token)
        return token
      } catch (err) {
        if (!isMountedRef.current) return null

        const errorObj = err instanceof Error ? err : new Error(String(err))
        console.error('reCAPTCHA 예외 발생:', errorObj)

        if (onError) {
          onError(errorObj)
        }
        return null
      }
    }, [executeRecaptcha, action, onVerify, onError])

    useImperativeHandle(
      ref,
      () => ({
        executeRecaptcha: handleReCaptchaVerify,
      }),
      [handleReCaptchaVerify],
    )
    useEffect(() => {
      return () => {
        isMountedRef.current = false
      }
    }, [])

    return null
  },
)

RecaptchaVerifier.displayName = 'RecaptchaVerifier'

export default RecaptchaVerifier
